// What the spotlight's unit displays read off a unit, computed the way the current card (inlineUnit.vue) does:
// stats and gear from unitCombatStats, evasion by direction from lib/evasion.js, where that evasion comes from, and the
// gear's elemental profile. Plus the gear, element and skill lists the unit and team cards show.
import { unitCombatStats } from '../../lib/unitStats.js';
import { hitChance } from '../../lib/evasion.js';
import { unitElementProfile } from '../../lib/elements.js';
import { skillRarity } from '../../lib/skillRarity.js';
import skillRarityData from '../../data/skillRarity.json';
import { ICONS_BASE } from '../../lib/assetUrls.js';

const SLOTS = [['Mainhand', 'Right hand'], ['Offhand', 'Left hand'], ['Head', 'Head'], ['Armor', 'Body'], ['Accessory', 'Accessory']];
const BONUS = [['hp', 'HP'], ['mp', 'MP'], ['pa', 'PA'], ['ma', 'MA'], ['speed', 'Speed'], ['move', 'Move'], ['jump', 'Jump']];
export const ELEMENT_KINDS = [['absorb', 'Absorbs'], ['cancel', 'Immune to'], ['half', 'Halves'], ['weak', 'Weak to'], ['strengthen', 'Boosts']];

export function unitInfo(unit, gd) {
  const { classBase, equipped, stats } = unitCombatStats(unit, gd.charStats, gd.itemschanged, gd.notAbles);
  const cEv = (classBase && classBase.cEvPercent) || 0;
  const t = { equipped, cEv, reaction: unit.ReactionSkill };
  const ev = (opts) => Math.round(100 - hitChance(t, opts));
  const evade = { front: ev({ type: 'physical', direction: 'front' }), side: ev({ type: 'physical', direction: 'side' }), back: ev({ type: 'physical', direction: 'back' }), magic: ev({ type: 'magical' }) };
  const sources = [];
  if (cEv) sources.push(`${unit.Class} ${cEv}%`);
  if (equipped.Offhand.type === 'Shield' && (equipped.Offhand.pevade || equipped.Offhand.mevade)) sources.push(`${unit.Offhand} ${Math.max(equipped.Offhand.pevade, equipped.Offhand.mevade)}%`);
  if (equipped.Accessory.pevade || equipped.Accessory.mevade) sources.push(`${unit.Accessory} ${Math.max(equipped.Accessory.pevade, equipped.Accessory.mevade)}%`);
  const parry = [equipped.Mainhand.wevade, equipped.Offhand.wevade].filter(Boolean);
  if (unit.ReactionSkill === 'Parry' && parry.length) sources.push('Parry ' + parry.map((w) => w + '%').join(' + '));
  return { classBase, equipped, stats, evade, sources, elements: unitElementProfile(equipped) };
}

// The unit's gear, one row per slot, with what each piece adds (WP, evasion, stat bonuses, element). Empty slots are
// left out (bar the weapon hand) unless `all`. Each row carries its unit field (`key`: Mainhand, Offhand, …).
export function gearRows(unit, gd, all = false) {
  const it = gd.itemschanged;
  return SLOTS.map(([k, slot]) => {
    const name = unit[k] || '', item = name ? it[name] : null;
    const bits = [];
    if (item) {
      if (item.wp) bits.push('WP ' + item.wp);
      if (item.pevade || item.mevade) bits.push(`Evade ${item.pevade || 0}/${item.mevade || 0}`);
      if (item.wevade) bits.push('W-Ev ' + item.wevade);
      for (const [b, label] of BONUS) if (item[b]) bits.push(`${label} +${item[b]}`);
      if (item.element) bits.push(item.element);
    }
    return { key: k, slot, name, icon: ICONS_BASE + name.replace(/\s+/g, '') + '.gif', line: bits.join(' · ') };
  }).filter((g) => all || g.name || g.slot === 'Right hand');
}

// An element profile (unitInfo's `elements`) as chips: "Absorbs Fire", "Weak to Ice", …
export function elementChips(e) {
  const out = [];
  for (const [k, label] of ELEMENT_KINDS) for (const el of e[k]) out.push({ k, label, el });
  return out;
}

// The unit's action skills by set, each with its roll rarity. A monster's ClassSkills are empty in the tournament data,
// so with game data (`gd.monsterSkills`) its own kit is listed instead; a * skill needs a Beastmaster ally.
export function skillSets(u, gd) {
  const out = [];
  const mon = u.Gender === 'Monster' && gd && gd.monsterSkills && gd.monsterSkills[u.Class];
  if (mon && mon.length) out.push({ title: 'Monster skills', skills: mon.map((n) => ({ name: n.replace(/\*$/, '') + (n.endsWith('*') ? ' ★' : ''), tier: null, roll: null, beast: n.endsWith('*') })) });
  const mk = (names, secondary) => names.map((n) => { const r = skillRarity(n, secondary, skillRarityData); return { name: n, tier: r && r.tier, roll: r && r.roll }; });
  if ((u.ClassSkills || []).length) out.push({ title: u.Class + ' skills', skills: mk(u.ClassSkills, false) });
  if ((u.ExtraSkills || []).length) out.push({ title: u.ActionSkill || 'Secondary', skills: mk(u.ExtraSkills, true) });
  return out;
}
